import { CheckCircle, UserPlus, X, CheckSquare, Square, MinusSquare, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'

/**
 * 批量操作结果
 * Requirements: 5.4
 */
export interface BatchOperationResult {
  total: number
  success: number
  failed: number
  errors?: Array<{ id: string; message: string }>
}

/**
 * 批量操作栏属性
 * Requirements: 5.1, 5.2, 5.3, 5.4
 */
export interface BatchOperationBarProps {
  /** 选中数量 */
  selectedCount: number
  /** 总数量 */
  totalCount?: number
  /** 是否全选 */
  isAllSelected: boolean
  /** 是否部分选中 */
  isPartiallySelected: boolean
  /** 全选回调 */
  onSelectAll: () => void
  /** 清除选择回调 */
  onClearSelection: () => void
  /** 批量审批回调 */
  onBatchApprove?: () => void
  /** 批量分配回调 */
  onBatchAssign?: () => void
  /** 是否正在审批 */
  isApproving?: boolean
  /** 是否正在分配 */
  isAssigning?: boolean
  /** 是否可以审批 */
  canApprove?: boolean
  /** 是否可以分配 */
  canAssign?: boolean
  /** 操作进度 (0-100) */
  progress?: number
  /** 操作结果 */
  operationResult?: BatchOperationResult | null
  /** 关闭结果回调 */
  onDismissResult?: () => void
  /** 自定义类名 */
  className?: string
}

/**
 * 批量操作栏组件
 * 用于工作流任务的批量操作
 * Requirements: 5.1, 5.2, 5.3, 5.4
 */
export function BatchOperationBar({
  selectedCount,
  totalCount,
  isAllSelected,
  isPartiallySelected,
  onSelectAll,
  onClearSelection,
  onBatchApprove,
  onBatchAssign,
  isApproving = false,
  isAssigning = false,
  canApprove = true,
  canAssign = true,
  progress,
  operationResult,
  onDismissResult,
  className,
}: BatchOperationBarProps) {
  const isOperating = isApproving || isAssigning
  const showProgress = isOperating && progress !== undefined

  // 如果没有选中项且没有操作结果，不显示工具栏
  if (selectedCount === 0 && !operationResult) {
    return null
  }

  const CheckboxIcon = isAllSelected
    ? CheckSquare
    : isPartiallySelected
      ? MinusSquare
      : Square

  // 显示操作结果
  if (operationResult) {
    const hasErrors = operationResult.failed > 0
    const allSuccess = operationResult.failed === 0

    return (
      <div
        className={cn(
          'flex items-center gap-4 rounded-lg border px-4 py-2',
          'animate-in slide-in-from-top-2 duration-200',
          allSuccess ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' : 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800',
          className
        )}
      >
        {allSuccess ? (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-amber-600" />
        )}

        <div className="flex-1">
          <div className="text-sm font-medium">
            {allSuccess
              ? `成功处理 ${operationResult.success} 项`
              : `成功 ${operationResult.success} 项，失败 ${operationResult.failed} 项`}
          </div>
          {hasErrors && operationResult.errors && operationResult.errors.length > 0 && (
            <div className="text-xs text-muted-foreground mt-1">
              {operationResult.errors.slice(0, 3).map((err, i) => (
                <div key={i}>{err.message}</div>
              ))}
              {operationResult.errors.length > 3 && (
                <div>还有 {operationResult.errors.length - 3} 个错误...</div>
              )}
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onDismissResult}
          className="h-8"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center gap-4 rounded-lg border bg-muted/50 px-4 py-2',
        'animate-in slide-in-from-top-2 duration-200',
        className
      )}
    >
      {/* 选择状态 */}
      <div className="flex items-center gap-2">
        <button
          onClick={isAllSelected ? onClearSelection : onSelectAll}
          className="flex items-center gap-2 text-sm hover:text-primary"
          disabled={isOperating}
        >
          <CheckboxIcon className="h-4 w-4" />
          <span>
            已选择 <strong>{selectedCount}</strong>
            {totalCount !== undefined && ` / ${totalCount}`} 项
          </span>
        </button>
      </div>

      {/* 分隔线 */}
      <div className="h-4 w-px bg-border" />

      {/* 进度指示器 */}
      {showProgress && (
        <div className="flex items-center gap-2 min-w-[120px]">
          <Progress value={progress} className="h-2 flex-1" />
          <span className="text-xs text-muted-foreground">{progress}%</span>
        </div>
      )}

      {/* 批量操作按钮 */}
      {!showProgress && (
        <div className="flex items-center gap-2">
          {onBatchApprove && (
            <Button
              variant="outline"
              size="sm"
              disabled={!canApprove || isOperating}
              onClick={onBatchApprove}
              className="h-8"
            >
              {isApproving ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="mr-1.5 h-4 w-4" />
              )}
              {isApproving ? '审批中...' : '批量审批'}
            </Button>
          )}
          {onBatchAssign && (
            <Button
              variant="outline"
              size="sm"
              disabled={!canAssign || isOperating}
              onClick={onBatchAssign}
              className="h-8"
            >
              {isAssigning ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="mr-1.5 h-4 w-4" />
              )}
              {isAssigning ? '分配中...' : '批量分配'}
            </Button>
          )}
        </div>
      )}

      {/* 清除选择按钮 */}
      <div className="ml-auto">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
          disabled={isOperating}
          className="h-8 text-muted-foreground hover:text-foreground"
        >
          <X className="mr-1.5 h-4 w-4" />
          取消选择
        </Button>
      </div>
    </div>
  )
}

/**
 * 任务选择复选框
 */
export interface TaskCheckboxProps {
  /** 是否选中 */
  checked: boolean
  /** 变更回调 */
  onChange: (checked: boolean) => void
  /** 是否禁用 */
  disabled?: boolean
  /** 自定义类名 */
  className?: string
}

export function TaskCheckbox({
  checked,
  onChange,
  disabled,
  className,
}: TaskCheckboxProps) {
  return (
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className={cn(
        'h-4 w-4 rounded border-gray-300',
        'text-primary focus:ring-primary focus:ring-offset-0',
        'cursor-pointer disabled:cursor-not-allowed',
        className
      )}
    />
  )
}
